import React, { useCallback, useEffect, useRef, useState } from 'react'
import { QrCode, X } from 'lucide-react'
import { extractProductIdFromScanPayload } from '../../../utils/scanPayload'
import { reportError } from '../../../utils/reportError'
import { trackUserAction } from '../../../utils/telemetry'
import { useQrCameraScanner } from '../../../hooks/useQrCameraScanner'
import { QrCameraScannerPanel } from '../../../components/qr/QrCameraScannerPanel'
import { NativeModalDialog } from './NativeModalDialog'
import styles from '../CreateTransaction.module.css'

import type { AddScannedProductResult } from '../../../services/scanToTrade'

type QrScannerModalProps = {
  open: boolean
  onClose: () => void
  onScanProductId: (productId: string) => Promise<AddScannedProductResult>
}

export const QrScannerModal: React.FC<QrScannerModalProps> = ({ open, onClose, onScanProductId }) => {
  const [scannerStatus, setScannerStatus] = useState('Point camera at a QR code')
  const [scannerError, setScannerError] = useState<string | null>(null)
  const [scannerSuccess, setScannerSuccess] = useState<string | null>(null)
  const [manualScanInput, setManualScanInput] = useState('')
  const [showManualEntry, setShowManualEntry] = useState(false)
  const [recentScans, setRecentScans] = useState<string[]>([])

  const scannerVideoRef = useRef<HTMLVideoElement | null>(null)
  const scannerBusyRef = useRef(false)
  const scannerCooldownRef = useRef(0)

  const handleDetectedScan = useCallback(
    async (productId: string): Promise<AddScannedProductResult | null> => {
      const nowTs = Date.now()
      if (scannerBusyRef.current || nowTs - scannerCooldownRef.current < 1200) {
        return null
      }

      scannerBusyRef.current = true
      scannerCooldownRef.current = nowTs
      setScannerError(null)
      setScannerSuccess(null)
      setScannerStatus(`Adding ${productId.slice(0, 8).toUpperCase()}...`)

      try {
        const result = await onScanProductId(productId)
        setRecentScans((prev) => [productId, ...prev.filter((id) => id !== productId)].slice(0, 6))
        const successText = result.merged
          ? `✓ ${result.itemName} quantity is now ${result.quantity}. Scan the next QR.`
          : `✓ ${result.itemName} added. Move to the next QR code.`
        setScannerSuccess(successText)
        setScannerStatus('Ready for next scan')
        return result
      } catch (err: unknown) {
        const anyErr = err as { response?: { data?: { message?: string; error?: string } }; message?: string }
        const message =
          anyErr?.response?.data?.message ||
          anyErr?.response?.data?.error ||
          anyErr?.message ||
          'Could not add scanned card. Please try again.'
        reportError(err, { page: 'CreateTransaction', action: 'scannerQuickAdd' })
        setScannerError(message)
        setScannerStatus('Scan paused until next QR...')
        throw err
      } finally {
        scannerBusyRef.current = false
      }
    },
    [onScanProductId],
  )

  const onQrCameraRawValue = useCallback(
    (raw: string) => {
      const productId = extractProductIdFromScanPayload(raw)
      if (productId) {
        void handleDetectedScan(productId)
        return
      }
      const t = raw.trim()
      if (t.length > 3) {
        trackUserAction('scan_payload_unrecognized', { source: 'camera', len: t.length })
      }
    },
    [handleDetectedScan],
  )

  const onSessionStart = useCallback(() => {
    setScannerError(null)
    setScannerSuccess(null)
  }, [])

  useEffect(() => {
    if (!open) return
    setManualScanInput('')
    setShowManualEntry(false)
  }, [open])

  useEffect(() => {
    if (!open || !scannerError) return
    const msg = scannerError.toLowerCase()
    if (msg.includes('camera') || msg.includes('permission') || msg.includes('not supported')) {
      setShowManualEntry(true)
    }
  }, [open, scannerError])

  const { torchSupported, torchOn, toggleTorch, flipCameraSupported, flipCamera } = useQrCameraScanner({
    active: open,
    videoRef: scannerVideoRef,
    onRawValue: onQrCameraRawValue,
    setStatus: setScannerStatus,
    setCameraError: setScannerError,
    compatibilityStatus: 'Compatibility scanner active (slower, but works on more phones)',
    onSessionStart,
  })

  const handleManualQrAdd = async () => {
    const productId = extractProductIdFromScanPayload(manualScanInput)
    if (!productId) {
      setScannerError('Enter a valid scan URL or product ID.')
      if (manualScanInput.trim().length > 0) {
        trackUserAction('scan_payload_unrecognized', { source: 'manual', len: manualScanInput.trim().length })
      }
      return
    }

    setScannerError(null)
    try {
      const result = await handleDetectedScan(productId)
      if (result) setManualScanInput('')
    } catch {
      /* error UI already set */
    }
  }

  return (
    <NativeModalDialog open={open} onClose={onClose} className={styles.nativeModalDialog}>
      <div className={`${styles.modal} ${styles.qrScannerModal}`} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <div className={styles.modalHeaderContent}>
            <QrCode size={20} />
            <h3>Scan Cards into Transaction</h3>
          </div>
          <button type="button" onClick={onClose} aria-label="Close scanner">
            <X size={20} />
          </button>
        </div>

        <div className={styles.modalContent}>
          <QrCameraScannerPanel
            variant="staff"
            videoRef={scannerVideoRef}
            statusId="trade-qr-scanner-status"
            videoAriaLabel="Camera viewfinder for scanning QR codes"
            scannerStatus={scannerStatus}
            scannerSuccess={scannerSuccess}
            scannerError={scannerError}
            torchSupported={torchSupported}
            torchOn={torchOn}
            toggleTorch={toggleTorch}
            flipCameraSupported={flipCameraSupported}
            flipCamera={flipCamera}
            manualScanInput={manualScanInput}
            onManualScanInputChange={setManualScanInput}
            onManualSubmit={() => void handleManualQrAdd()}
            recentScans={recentScans}
            manualEntryVariant="collapsible"
            showManualEntry={showManualEntry}
            onRevealManualEntry={() => setShowManualEntry(true)}
            hint={(
              <p
                style={{ margin: 0 }}
                title="Each QR scan adds the card to this trade. Scan the same QR again to increase quantity. You can always adjust quantities or remove cards in the basket."
              >
                Scanning a QR adds it to this trade. Scan the same card again if you need multiples; you can edit/remove anytime.
              </p>
            )}
          />
        </div>

        <div className={styles.modalActions}>
          <button type="button" className={styles.cancelBtn} onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </NativeModalDialog>
  )
}
